import { useEffect, useMemo, useState } from "react";
import { api, ApiError } from "../../api";
import type { Measurement } from "../../types";
import { MEASUREMENT_FIELDS } from "../../types";
import { useTelegramBackButton } from "../../hooks/useTelegramBackButton";
import { tg } from "../../telegram";
import { ymd, fmtSlot } from "../../utils/date";
import { useT } from "../../i18n";

export default function MyMeasurements() {
  const { t, lang } = useT();
  const today = useMemo(() => ymd(new Date()), []);
  const sevenAgo = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - 7);
    return ymd(d);
  }, []);
  const [from, setFrom] = useState<string>(sevenAgo);
  const [to, setTo] = useState<string>(today);
  const [items, setItems] = useState<Measurement[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Measurement | null>(null);

  useEffect(() => {
    setError(null);
    setItems(null);
    const q = new URLSearchParams({ from, to });
    api.get<Measurement[]>(`/api/me/measurements?${q.toString()}`)
      .then(setItems)
      .catch((e: ApiError) => setError(e.message));
  }, [from, to]);

  const downloadXlsx = () => {
    const q = new URLSearchParams({ from, to, lang });
    api.get<Blob>(`/api/me/export?${q.toString()}`)
      .then(blob => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `energo-mine-${from}_to_${to}.xlsx`;
        a.click();
        URL.revokeObjectURL(url);
      })
      .catch((e: ApiError) => setError(e.message));
  };

  return (
    <div>
      <h1>{t("my_records.title")}</h1>

      <div className="card">
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 8 }}>
          <label>
            <div style={{ fontSize: 12, color: "var(--hint)" }}>{t("records.from")}</div>
            <input type="date" value={from} onChange={e => setFrom(e.target.value)} />
          </label>
          <label>
            <div style={{ fontSize: 12, color: "var(--hint)" }}>{t("records.to")}</div>
            <input type="date" value={to} onChange={e => setTo(e.target.value)} />
          </label>
        </div>
        <button className="btn excel" onClick={downloadXlsx}>{t("my_records.download_xlsx")}</button>
        <div style={{ fontSize: 11, color: "var(--hint)", marginTop: 8 }}>
          {t("records.xlsx_per_day")}
        </div>
      </div>

      {error && <div className="alert error">{error}</div>}
      {!items && !error && <div className="empty-state"><span className="spinner" /></div>}
      {items && items.length === 0 && (
        <div className="empty-state">{t("common.no_data")}</div>
      )}

      {items && items.length > 0 && (
        <div className="scroll-x">
          <table className="table">
            <thead>
              <tr>
                <th>{t("records.col_slot")}</th>
                <th>{t("records.col_type")}</th>
                <th>{t("records.col_status")}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {items.map(m => {
                const cls = m.is_emergency ? "emergency"
                          : m.status === "missed" ? "missed"
                          : m.is_late ? "late" : "";
                return (
                  <tr key={m.id} className={cls}>
                    <td>{fmtSlot(m.slot_time)}</td>
                    <td>{m.is_emergency ? "🚨" : m.status === "missed" ? "🚫" : m.is_late ? "🟨" : "📋"}</td>
                    <td>{m.status === "completed" ? t("status.completed") : m.status === "missed" ? t("status.miss_short") : m.status}</td>
                    <td><button className="period-tab" onClick={() => setSelected(m)}>›</button></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {selected && <Detail m={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}


function Detail({ m, onClose }: { m: Measurement; onClose: () => void }) {
  const { t } = useT();
  useTelegramBackButton(onClose, [onClose]);
  useEffect(() => { tg()?.HapticFeedback?.impactOccurred("light"); }, []);
  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 200,
      display: "flex", alignItems: "flex-end",
    }} onClick={onClose}>
      <div style={{
        background: "var(--bg)", width: "100%", maxHeight: "85vh", overflowY: "auto",
        borderRadius: "16px 16px 0 0", padding: "16px",
      }} onClick={e => e.stopPropagation()}>
        <h2 style={{ marginTop: 0 }}>
          {t("records.detail.title", { id: m.id })} {m.is_emergency ? "🚨" : m.status === "missed" ? "🚫" : ""}
        </h2>
        <div style={{ fontSize: 13, color: "var(--hint)", marginBottom: 12 }}>
          {t("records.detail.slot")}: {fmtSlot(m.slot_time)}<br/>
          {m.started_at && <>{t("records.detail.started")}: {fmtSlot(m.started_at)}<br/></>}
          {m.completed_at && <>{t("records.detail.completed")}: {fmtSlot(m.completed_at)}<br/></>}
          {m.response_time_sec !== null && <>{t("records.detail.response")}: {m.response_time_sec} {t("records.detail.seconds")}<br/></>}
          {m.fill_duration_sec !== null && <>{t("records.detail.fill")}: {m.fill_duration_sec} {t("records.detail.seconds")}<br/></>}
          {m.is_emergency && m.emergency_reason && <>{t("records.detail.reason")}: <i>{m.emergency_reason}</i><br/></>}
        </div>
        <table className="table">
          <tbody>
            {MEASUREMENT_FIELDS.map(f => {
              const v = (m as unknown as Record<string, unknown>)[f.key];
              return (
                <tr key={f.key}>
                  <td style={{ width: "60%" }}>{f.label}{f.unit ? ` (${f.unit})` : ""}</td>
                  <td style={{ fontFamily: "ui-monospace, monospace" }}>{v == null ? "—" : String(v)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <button className="btn" style={{ marginTop: 12 }} onClick={onClose}>{t("common.close")}</button>
      </div>
    </div>
  );
}
